import { useCallback, useEffect, useState } from 'react';

import type { PushSubscriptionDto } from '@llura/types';

import { api } from '../lib/api-client';

type Status = 'idle' | 'unsupported' | 'denied' | 'subscribed' | 'subscribing';

/** Browser permission state, mirrored for the settings status panel. */
export type NotificationPermissionState = 'granted' | 'denied' | 'default';

interface UsePushSubscriptionResult {
  status: Status;
  /** Raw browser permission — 'default' means never asked. */
  permission: NotificationPermissionState;
  /** True once this device has an active push subscription. */
  isSubscribed: boolean;
  /** True when the browser can't do web push at all. */
  unsupported: boolean;
  subscribe: () => Promise<void>;
}

function readPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'default';
  return Notification.permission as NotificationPermissionState;
}

export function usePushSubscription(): UsePushSubscriptionResult {
  const [status, setStatus] = useState<Status>('idle');
  const [permission, setPermission] = useState<NotificationPermissionState>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    const perm = readPermission();
    setPermission(perm);
    if (perm === 'denied') setStatus('denied');

    // Reflect an existing subscription so the status panel is accurate on load.
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (cancelled) return;
        if (sub) {
          setIsSubscribed(true);
          setStatus('subscribed');
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const subscribe = useCallback(async () => {
    if (status === 'unsupported' || status === 'subscribing') return;
    setStatus('subscribing');

    const perm = await Notification.requestPermission();
    setPermission(perm as NotificationPermissionState);
    if (perm !== 'granted') {
      setStatus('denied');
      return;
    }

    // Prefer the key served by the API (single source of truth) and fall back
    // to the build-time env var for offline/dev builds.
    let vapidPublicKey = '';
    try {
      vapidPublicKey = (await api.notifications.vapidKey()).key ?? '';
    } catch {
      // ignore — fall through to the env var below
    }
    if (!vapidPublicKey) {
      vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';
    }
    if (!vapidPublicKey) {
      console.warn('[push] no VAPID public key available — skipping subscription');
      setStatus('idle');
      return;
    }

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // Credentialed POST so the API attaches the signed-in user to the
    // subscription (only user-linked subscriptions are targetable by reminders).
    const dto: PushSubscriptionDto = JSON.parse(JSON.stringify(sub));
    await api.notifications.subscribe(dto);

    setIsSubscribed(true);
    setStatus('subscribed');
  }, [status]);

  return {
    status,
    permission,
    isSubscribed,
    unsupported: status === 'unsupported',
    subscribe,
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
